import React from 'react';
import styled from 'styled-components';
import { FormControl } from 'react-bootstrap';
import Button from './Button';
import { uiTokens } from './tokens';

type SearchSize = 'md' | 'lg';
type SearchVariant = 'glass' | 'hero';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
  loading?: boolean;
  leftSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
  size?: SearchSize;
  variant?: SearchVariant;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

const Shell = styled.div<{ $size: SearchSize; $variant: SearchVariant }>`
  display: flex;
  align-items: stretch;
  gap: 0;
  width: 100%;
  border-radius: ${uiTokens.radius.pill};
  padding: ${({ $size }) => ($size === 'lg' ? '6px 8px 6px 18px' : '4px 6px 4px 14px')};
  background: ${({ $variant }) => ($variant === 'hero' ? 'rgba(10, 10, 12, 0.72)' : 'rgba(15, 23, 42, 0.45)')};
  border: 1px solid ${({ $variant }) => ($variant === 'hero' ? uiTokens.colors.borderSoft : uiTokens.colors.borderMuted)};
  box-shadow: ${uiTokens.shadow.md};
  backdrop-filter: blur(10px);

  .form-control {
    flex: 1;
    min-width: 0;
    border: none;
    background: transparent;
    color: #f4f4f5;
    font-size: ${({ $size }) => ($size === 'lg' ? '16px' : '15px')};
    box-shadow: none;

    &::placeholder {
      color: rgba(212, 212, 216, 0.7);
    }

    &:focus {
      border: none;
      box-shadow: none;
      background: transparent;
      color: #f4f4f5;
    }
  }
`;

const Slot = styled.div`
  display: inline-flex;
  align-items: center;
  margin-right: 8px;
`;

export const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  onSubmit,
  placeholder,
  loading,
  leftSlot,
  rightSlot,
  size = 'lg',
  variant = 'hero',
  disabled,
  className,
  ariaLabel = 'Search',
}) => {
  return (
    <Shell className={className} $size={size} $variant={variant}>
      {leftSlot ? <Slot>{leftSlot}</Slot> : null}
      <FormControl
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit();
          }
        }}
      />
      {rightSlot}
      <Button
        type="button"
        iconOnly
        loading={loading}
        disabled={disabled}
        onClick={onSubmit}
        aria-label="Submit search"
      />
    </Shell>
  );
};

export default SearchInput;
